import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { DialogHeading } from 'components/dialog-title';
import { Button, ButtonProps, Dialog, DialogContent, Tooltip, Typography } from '@mui/material';
import { UserPatientProfile } from 'services/user-patient-profile';
import { RowField } from '../../../row-field';
import { ControllerRenderProps, useController, useFormContext } from 'react-hook-form';
import { differenceInYears } from 'date-fns';
import { convertToDate } from 'utils/dates';
import EditIcon from '@mui/icons-material/Edit';
import { useOpen } from 'AurionCR/components/hooks';
import { FormFields, Left } from 'utils/types';
import { useTranslate } from 'hooks/use-translate';
import { AppInput } from 'components/app-input';
import { useFieldProps } from 'hooks';
import { AppPhoneInput } from 'components/app-phone-input';
import { composeFunctions } from 'utils';
import WarningIcon from '@mui/icons-material/Warning';
import style from './index.module.scss';
import clsx from 'clsx';
import { useFactoryChangeInput, usePatientData } from '../../hooks';
import { getPatientFieldsMaxLength } from '../../helpers';
import { PatientFormModel } from '../../models';

interface PreviewItemProps {
  label: React.ReactNode;
  children: React.ReactNode;
}
const PreviewRowItem: React.FC<PreviewItemProps> = ({ label, children }) => {
  return (
    <tr>
      <td>
        <Typography>{label}:</Typography>
      </td>
      <td>
        <Typography>{children}</Typography>
      </td>
    </tr>
  );
};

type Model = Pick<
  Left<UserPatientProfile>,
  | 'familyMemberFirstName'
  | 'familyMemberLastName'
  | 'familyMemberPhone'
  | 'familyMemberIDNumber'
  | 'familyMemberRelationship'
  | 'dateOfBirth'
>;

const TooltipTitle: React.FC<unknown> = () => {
  const { t } = useTranslate();
  const { watch } = useFormContext<FormFields<Model>>();
  const familyMemberFirstName = watch('familyMemberFirstName');
  const familyMemberLastName = watch('familyMemberLastName');
  const familyMemberPhone = watch('familyMemberPhone');
  const familyMemberIDNumber = watch('familyMemberIDNumber');
  const familyMemberRelationship = watch('familyMemberRelationship');

  return (
    <table>
      <tbody>
        <PreviewRowItem label={t('first-name')}>{familyMemberFirstName}</PreviewRowItem>
        <PreviewRowItem label={t('last-name')}>{familyMemberLastName}</PreviewRowItem>
        <PreviewRowItem label={t('phone')}>{familyMemberPhone}</PreviewRowItem>
        <PreviewRowItem label={t('id-number')}>{familyMemberIDNumber}</PreviewRowItem>
        <PreviewRowItem label={t('relationship')}>{familyMemberRelationship}</PreviewRowItem>
      </tbody>
    </table>
  );
};

const FIELDS: (keyof Model)[] = [
  'familyMemberFirstName',
  'familyMemberLastName',
  'familyMemberPhone',
  'familyMemberIDNumber',
  'familyMemberRelationship',
];

interface Props extends ButtonProps {
  debounce: boolean;
}

export const ButtonFieldMember = memo<Props>(({ debounce, ...buttonProps }) => {
  const {
    control,
    formState: { errors },
    watch,
    getValues,
    trigger,
  } = useFormContext<FormFields<Model>>();
  const { onUpdateDebounce, onUpdate, patient } = usePatientData();
  const { t } = useTranslate();

  const factoryChangeInput = useFactoryChangeInput({
    onUpdate: debounce ? onUpdateDebounce : onUpdate,
    initData: patient,
    getValues,
    validate: trigger,
  });

  const dateOfBirth = watch('dateOfBirth');

  const { isOpen, handleOpen, handleClose } = useOpen();

  const isDisabled = useMemo(() => {
    if (!dateOfBirth) {
      return true;
    }
    return differenceInYears(new Date(), convertToDate(dateOfBirth)) > 17;
  }, [dateOfBirth]);

  const getFieldProps = useFieldProps({ errors, emptyHelperText: '' });
  const renderInput = (renderProps: ControllerRenderProps<FormFields<Model>>) => {
    const { label, ref: _fieldRef, ...rest } = getFieldProps(renderProps);
    const maxLength = getPatientFieldsMaxLength(renderProps.name as keyof PatientFormModel);
    return (
      <RowField label={label}>
        <AppInput
          {...rest}
          onBlur={composeFunctions(rest.onBlur, factoryChangeInput(rest.name, label))}
          slotProps={{
            htmlInput: {
              maxLength,
            },
          }}
        />
      </RowField>
    );
  };

  const ref = useRef<HTMLButtonElement>(null);

  const onFocus = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
  }, [ref]);

  const controllerFirstName = useController<Model>({ name: 'familyMemberFirstName', control });
  const controllerLastName = useController<Model>({ name: 'familyMemberLastName', control });
  const controllerMemberPhone = useController<Model>({ name: 'familyMemberPhone', control });
  const controllerIdNumber = useController<Model>({ name: 'familyMemberIDNumber', control });
  const controllerRelationship = useController<Model>({
    name: 'familyMemberRelationship',
    control,
  });

  // RHF 6 took `onFocus` on useController so that focus-on-error landed on this
  // button (the inputs live in a closed dialog). RHF 7 focuses whatever `field.ref`
  // registered, so the button is registered as that target and the inputs do not
  // receive the field ref (see the spreads below).
  const focusTarget = useMemo(() => ({ focus: onFocus }), [onFocus]);
  const fieldRefs = [
    controllerFirstName.field.ref,
    controllerLastName.field.ref,
    controllerMemberPhone.field.ref,
    controllerIdNumber.field.ref,
    controllerRelationship.field.ref,
  ];
  useEffect(() => {
    fieldRefs.forEach((registerRef) => registerRef(focusTarget));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTarget, ...fieldRefs]);

  const hasError = FIELDS.some((field) => {
    return Boolean(errors[field]);
  });

  return (
    <>
      <Tooltip title={<TooltipTitle />} arrow>
        <span>
          <Button
            ref={ref}
            variant={'contained'}
            color={hasError ? undefined : 'primary'}
            size={'small'}
            startIcon={hasError ? <WarningIcon /> : <EditIcon />}
            {...buttonProps}
            className={clsx(style.button, hasError && style.buttonError)}
            onClick={handleOpen}
            disabled={isDisabled}
          >
            {t('edit-family-member')}
          </Button>
        </span>
      </Tooltip>

      <Dialog open={isOpen} fullWidth maxWidth={'md'} onClose={handleClose}>
        <DialogHeading title={t('family-member')} onClose={handleClose} />
        <DialogContent>
          {renderInput(controllerFirstName.field)}
          {renderInput(controllerLastName.field)}
          <RowField label={t('family-member-phone')}>
            <AppPhoneInput
              {...getFieldProps(controllerMemberPhone.field)}
              ref={undefined}
              label={null}
              onBlur={composeFunctions(
                controllerMemberPhone.field.onBlur,
                factoryChangeInput(controllerMemberPhone.field.name, t('family-member-phone')),
              )}
            />
          </RowField>
          {renderInput(controllerIdNumber.field)}
          {renderInput(controllerRelationship.field)}
        </DialogContent>
      </Dialog>
    </>
  );
});
